import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// fev/2026: MIGRADO de sessionStorage → localStorage. sessionStorage é limpo
// quando o usuário fecha a aba/PWA — resultado: ao voltar ao app, TODOS os chats
// pareciam ter mensagens não lidas novamente ("chat piscando"). localStorage
// persiste entre sessões e resolve o bug. TTL de 30 dias limpa automaticamente
// entradas antigas para não crescer indefinidamente.
const LS_KEY_PREFIX = "chat_last_read_"; // por entrega
const LS_TTL_MS = 30 * 24 * 3600 * 1000; // 30 dias
const POLL_MS = 12000;

const _storage = typeof window !== "undefined" ? window.localStorage : null;

/**
 * Toca um beep suave via Web Audio API (sem arquivo). Usado quando o poll
 * detecta mensagens NOVAS (count acima do baseline anterior). Falha silenciosa
 * se AudioContext estiver bloqueado (navegador exige interação prévia).
 */
function playChatBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    // Dois beeps rápidos (880Hz → 660Hz) — mais audível que um só e não estridente.
    [
      { freq: 880, start: 0, dur: 0.18 },
      { freq: 660, start: 0.22, dur: 0.18 },
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 800);
  } catch (e) {
    // AudioContext pode estar suspenso até o usuário interagir com a página.
    // Não faz sentido logar toda vez — é comportamento normal do navegador.
    if (process.env.NODE_ENV === "development") console.debug("chat beep bloqueado", e?.message);
  }
}

/**
 * Hook que retorna:
 *   unreadSet: Set<entrega_id> — entregas com mensagens não lidas pelo usuário atual
 *   markRead(entrega_id): registra que o usuário abriu/leu aquela conversa agora
 *   lastAt(entrega_id): timestamp da última mensagem (útil para snippets)
 *
 * Polling: 12s. A cada tick chama GET /api/chat/unread?since=lastPoll,
 * mesclando com o localStorage para persistir entre sessões.
 *
 * fev/2026: toca beep quando detecta MENSAGENS NOVAS (delta positivo no total).
 */
export function useChatUnread({ pollWhen = true } = {}) {
  const [unreadMap, setUnreadMap] = useState({}); // {entrega_id: {count, last_at, last_sender}}
  const lastPollRef = useRef(new Date(Date.now() - 24 * 3600 * 1000).toISOString());
  // baseline pra detectar mensagens NOVAS entre polls — soma total de counts.
  // Iniciamos com null (não emite beep no 1º carregamento do app).
  const totalBaselineRef = useRef(null);

  const readLocal = (eid) => {
    if (!_storage) return null;
    const val = _storage.getItem(LS_KEY_PREFIX + eid);
    if (!val) return null;
    // Limpa entradas com mais de 30 dias (housekeeping barato — só quando lê)
    try {
      const t = new Date(val).getTime();
      if (Number.isFinite(t) && Date.now() - t > LS_TTL_MS) {
        _storage.removeItem(LS_KEY_PREFIX + eid);
        return null;
      }
    } catch { /* ignora parse */ }
    return val;
  };

  const poll = useCallback(async () => {
    try {
      const since = lastPollRef.current;
      const r = await api.get(`/chat/unread?since=${encodeURIComponent(since)}`);
      lastPollRef.current = new Date().toISOString();
      let newTotal = 0;
      setUnreadMap((prev) => {
        const next = { ...prev };
        for (const row of r.data || []) {
          const readAt = readLocal(row.entrega_id);
          // ATENÇÃO: readAt está em UTC (Z) e row.last_at pode estar em BRT (-03:00).
          // Comparação de strings ISO com timezones diferentes é ERRADA — usar Date real.
          if (readAt && row.last_at) {
            const rt = new Date(readAt).getTime();
            const lt = new Date(row.last_at).getTime();
            if (Number.isFinite(rt) && Number.isFinite(lt) && rt >= lt) continue;
          }
          const existing = next[row.entrega_id] || { count: 0 };
          next[row.entrega_id] = {
            count: (existing.count || 0) + row.count,
            last_at: row.last_at,
            last_sender: row.last_sender,
          };
        }
        newTotal = Object.values(next).reduce((s, v) => s + (v.count || 0), 0);
        return next;
      });
      // Detecta MENSAGENS NOVAS: total cresceu desde o último poll.
      // No 1º poll (baseline = null), só define baseline sem tocar beep.
      const prevTotal = totalBaselineRef.current;
      if (prevTotal !== null && newTotal > prevTotal) {
        playChatBeep();
      }
      totalBaselineRef.current = newTotal;
    } catch (e) {
      console.warn("chat unread poll", e?.message);
    }
  }, []);

  useEffect(() => {
    if (!pollWhen) return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [pollWhen, poll]);

  const markRead = useCallback((eid) => {
    if (!eid) return;
    try { _storage?.setItem(LS_KEY_PREFIX + eid, new Date().toISOString()); }
    catch (e) { console.warn("localStorage indisponível", e); }
    setUnreadMap((prev) => {
      if (!prev[eid]) return prev;
      const { [eid]: _drop, ...rest } = prev;
      // Também ajusta o baseline pra não disparar beep no próximo poll por causa
      // do delta artificial que a leitura provocou.
      totalBaselineRef.current = Math.max(0, (totalBaselineRef.current || 0) - (_drop.count || 0));
      return rest;
    });
  }, []);

  return { unreadMap, markRead, refresh: poll };
}
